import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { designActions, templateActions } from '../../actions';
import { educationTaxonomyService } from '../../services';
import './Templates.css';

const EDUCATION_GRADES = [
  { key: 'educationPreschool', code: 'preschool', title: 'پیش‌دبستانی', value: 'پیش‌دبستانی', number: 'پ', color: '#ef6c9b' },
  { key: 'educationGrade1', code: 'grade-1', title: 'پایه اول', value: 'پایه اول', number: '۱', color: '#ffbf2f' },
  { key: 'educationGrade2', code: 'grade-2', title: 'پایه دوم', value: 'پایه دوم', number: '۲', color: '#ff8a65' },
  { key: 'educationGrade3', code: 'grade-3', title: 'پایه سوم', value: 'پایه سوم', number: '۳', color: '#54c7b0' },
  { key: 'educationGrade4', code: 'grade-4', title: 'پایه چهارم', value: 'پایه چهارم', number: '۴', color: '#58a6ff' },
  { key: 'educationGrade5', code: 'grade-5', title: 'پایه پنجم', value: 'پایه پنجم', number: '۵', color: '#9b7bff' },
  { key: 'educationGrade6', code: 'grade-6', title: 'پایه ششم', value: 'پایه ششم', number: '۶', color: '#f36d9b' },
  { key: 'educationGrade7', code: 'grade-7', title: 'پایه هفتم', value: 'پایه هفتم', number: '۷', color: '#49a8a1' },
  { key: 'educationGrade8', code: 'grade-8', title: 'پایه هشتم', value: 'پایه هشتم', number: '۸', color: '#f29f3f' },
  { key: 'educationGrade9', code: 'grade-9', title: 'پایه نهم', value: 'پایه نهم', number: '۹', color: '#6574cd' },
];

const publicThumbnail = source => {
  if (typeof source !== 'string') return '';
  return source.replace('/api/resources/', '/api/public/resources/');
};

const Templates = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const groups = useSelector(state => state.template.templates) || [];
  const loading = useSelector(state => state.template.status);
  const isLoggedIn = useSelector(state => state.authentication.loggedIn);
  const [activeGrade, setActiveGrade] = useState(EDUCATION_GRADES[0]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [activeSubject, setActiveSubject] = useState('');
  const [activeTopic, setActiveTopic] = useState('');
  const [templatePreview, setTemplatePreview] = useState(null);
  const [failedImages, setFailedImages] = useState({});

  useEffect(() => {
    educationTaxonomyService.findAllPublic().then(items => {
      setTaxonomy(Array.isArray(items) ? items : []);
    }).catch(() => setTaxonomy([]));
  }, []);

  const grades = useMemo(() => {
    const databaseGrades = taxonomy.filter(item => item.type === 'grade').map((item, index) => ({
      ...item,
      value: item.title,
      number: item.code === 'preschool' ? 'پ' : String(index).replace(/0/g, '۰').replace(/1/g, '۱').replace(/2/g, '۲').replace(/3/g, '۳').replace(/4/g, '۴').replace(/5/g, '۵').replace(/6/g, '۶').replace(/7/g, '۷').replace(/8/g, '۸').replace(/9/g, '۹'),
      color: EDUCATION_GRADES[index] ? EDUCATION_GRADES[index].color : '#487cc5',
    }));
    return databaseGrades.length ? databaseGrades : EDUCATION_GRADES;
  }, [taxonomy]);

  useEffect(() => {
    if (!grades.some(grade => grade.code === activeGrade.code)) setActiveGrade(grades[0]);
  }, [activeGrade.code, grades]);

  useEffect(() => {
    dispatch(
      templateActions.search(
        { groupBy: 'categories', perGroupLimit: 24, skip: 0, limit: grades.length },
        true,
      ),
    );
  }, [dispatch, grades.length]);

  const subjects = useMemo(() => taxonomy.filter(item => item.type === 'subject'
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(activeGrade.code))), [activeGrade.code, taxonomy]);
  const topics = useMemo(() => taxonomy.filter(item => item.type === 'topic'
    && (!activeSubject || item.subjectCode === activeSubject)
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(activeGrade.code))), [activeGrade.code, activeSubject, taxonomy]);

  const activeTemplates = useMemo(() => {
    if (!Array.isArray(groups)) return [];
    const group = groups.find(item => item && item._id === activeGrade.value);
    const templates = group && Array.isArray(group.templates) ? group.templates : [];
    return templates.filter(template => (
      (!activeSubject || template.data.education?.subjectCode === activeSubject)
      && (!activeTopic || (template.data.education?.topicCodes || []).includes(activeTopic))
    ));
  }, [activeGrade, activeSubject, activeTopic, groups]);

  const activeGradeTitle = activeGrade.title || activeGrade.value || t(activeGrade.key);

  const handleFreeDesign = () => {
    if (!isLoggedIn) {
      history.push('/#login');
      return;
    }
    dispatch(designActions.create({
      name: 'طراحی آزاد آموزشی',
      size: { width: 1600, height: 1600 },
      templateCategory: 'طراحی آزاد',
      educationContext: { mode: 'free', topicCodes: [] },
    }));
  };

  const handleUseTemplate = () => {
    if (!templatePreview) return;

    if (!isLoggedIn) {
      setTemplatePreview(null);
      history.push('/#login');
      return;
    }

    dispatch(
      designActions.create({
        templateId: templatePreview.data.id,
        templateCategory: activeGrade.value,
      }),
    );
  };

  return (
    <main className="educationTemplatesPage">
      <section className="educationTemplatesHero">
        <div className="educationTemplatesHeroCopy">
          <span className="educationTemplatesEyebrow">{t('educationPageEyebrow')}</span>
          <h1>{t('educationPageTitle')}</h1>
          <p>{t('educationPageDescription')}</p>
          <a className="educationTemplatesHeroCta" href="#education-grade-picker">
            {t('educationExplore')}
            <Icon name="arrow down" />
          </a>
        </div>
        <div className="educationTemplatesHeroVisual" aria-hidden="true">
          <div className="educationVisualCard educationVisualCardMath">
            <span>7 + 5</span>
            <Icon name="pencil alternate" />
          </div>
          <div className="educationVisualCard educationVisualCardClock">
            <span className="educationClockHand educationClockHour" />
            <span className="educationClockHand educationClockMinute" />
            <span className="educationClockDot" />
          </div>
          <div className="educationVisualCard educationVisualCardLetters">
            <span>آ</span>
            <small>A</small>
          </div>
          <span className="educationVisualSpark educationVisualSparkOne">✦</span>
          <span className="educationVisualSpark educationVisualSparkTwo">✦</span>
        </div>
      </section>

      <section className="educationTemplatesFacts" aria-label={t('educationPageFacts')}>
        <article>
          <span className="educationFactIcon"><Icon name="graduation cap" /></span>
          <div><strong>{t('educationNineGrades')}</strong><small>{t('educationNineGradesCaption')}</small></div>
        </article>
        <article>
          <span className="educationFactIcon"><Icon name="expand arrows alternate" /></span>
          <div><strong>{t('educationStandardSize')}</strong><small>{t('educationStandardSizeCaption')}</small></div>
        </article>
        <article>
          <span className="educationFactIcon"><Icon name="edit outline" /></span>
          <div><strong>{t('educationEditable')}</strong><small>{t('educationEditableCaption')}</small></div>
        </article>
      </section>

      <section id="education-grade-picker" className="educationGradeSection">
        <div className="educationSectionHeading">
          <span>{t('educationGradePickerEyebrow')}</span>
          <h2>{t('educationGradePickerTitle')}</h2>
          <p>{t('educationGradePickerDescription')}</p>
        </div>

        <div className="educationGradePicker" role="tablist" aria-label={t('educationGradePickerTitle')}>
          {grades.map(grade => {
            const selected = grade.code === activeGrade.code;
            return (
              <button
                key={grade.code || grade.key}
                type="button"
                role="tab"
                aria-selected={selected}
                className={selected ? 'educationGradeButton active' : 'educationGradeButton'}
                style={{ '--grade-color': grade.color }}
                onClick={() => { setActiveGrade(grade); setActiveSubject(''); setActiveTopic(''); }}
              >
                <span>{grade.number}</span>
                {grade.title || t(grade.key)}
              </button>
            );
          })}
          <button type="button" className="educationGradeButton educationFreeDesign" style={{ '--grade-color': '#173e7a' }} onClick={handleFreeDesign}>
            <span><Icon name="plus" /></span>
            طراحی آزاد
          </button>
        </div>

        {(subjects.length > 0 || topics.length > 0) && (
          <div className="educationLessonFilters">
            <label>درس
              <select value={activeSubject} onChange={event => { setActiveSubject(event.target.value); setActiveTopic(''); }}>
                <option value="">همه درس‌ها</option>
                {subjects.map(subject => <option key={subject.code} value={subject.code}>{subject.title}</option>)}
              </select>
            </label>
            <label>موضوع
              <select value={activeTopic} onChange={event => setActiveTopic(event.target.value)}>
                <option value="">همه موضوع‌ها</option>
                {topics.map(topic => <option key={topic.code} value={topic.code}>{topic.title}</option>)}
              </select>
            </label>
          </div>
        )}

        <div className="educationTemplatesResults" role="tabpanel">
          <div className="educationResultsHeading">
            <div>
              <span>{t('educationSelectedGrade')}</span>
              <h3>{t('educationGradeHeading', { grade: activeGradeTitle })}</h3>
            </div>
            {activeTemplates.length > 0 && (
              <span className="educationTemplateCount">
                {t('educationTemplateCount', { count: activeTemplates.length })}
              </span>
            )}
          </div>

          {loading === 'loading' ? (
            <div className="educationTemplateGrid" aria-busy="true">
              {[0, 1, 2, 3].map(item => <div key={item} className="educationTemplateSkeleton" />)}
            </div>
          ) : activeTemplates.length ? (
            <div className="educationTemplateGrid">
              {activeTemplates.map(template => {
                const imageFailed = failedImages[template.data.id];
                return (
                  <button
                    key={template.data.id}
                    type="button"
                    className="educationTemplateCard"
                    onClick={() => setTemplatePreview(template)}
                  >
                    <span className="educationTemplateImage">
                      {!imageFailed && (
                        <img
                          src={publicThumbnail(template.src)}
                          alt={template.data.name || activeGradeTitle}
                          loading="lazy"
                          onError={() => setFailedImages(previous => ({
                            ...previous,
                            [template.data.id]: true,
                          }))}
                        />
                      )}
                      {imageFailed && <Icon name="image outline" />}
                    </span>
                    <span className="educationTemplateCardContent">
                      <strong>{template.data.name || activeGradeTitle}</strong>
                      <small>{t('educationOpenPreview')} <Icon name="arrow left" /></small>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="educationEmptyState">
              <div className="educationEmptyIllustration" aria-hidden="true">
                <Icon name="book" />
                <span>✓</span>
              </div>
              <div>
                <h3>{t('educationEmptyTitle')}</h3>
                <p>{t('educationEmptyDescription', { grade: activeGradeTitle })}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="educationTemplatesBottomCta">
        <div>
          <span>{t('educationBottomEyebrow')}</span>
          <h2>{t('educationBottomTitle')}</h2>
          <p>{t('educationBottomDescription')}</p>
        </div>
        <Button as="a" href="/#login" className="educationBottomButton">
          {t('educationStartNow')}
        </Button>
      </section>

      <Modal
        size="small"
        open={Boolean(templatePreview)}
        onClose={() => setTemplatePreview(null)}
        className="educationTemplateModal"
      >
        {templatePreview && (
          <Modal.Content>
            <button
              type="button"
              className="educationModalClose"
              aria-label={t('close')}
              onClick={() => setTemplatePreview(null)}
            >
              <Icon name="close" />
            </button>
            <div className="educationModalImage">
              <img
                src={publicThumbnail(templatePreview.src)}
                alt={templatePreview.data.name || activeGradeTitle}
              />
            </div>
            <div className="educationModalContent">
              <span>{activeGradeTitle}</span>
              <h3>{templatePreview.data.name || activeGradeTitle}</h3>
              <p>{t('educationPreviewDescription')}</p>
              <Button onClick={handleUseTemplate}>{t('useTemplate')}</Button>
            </div>
          </Modal.Content>
        )}
      </Modal>
    </main>
  );
};

export default Templates;
